import React, { useState } from 'react';
import Header from '../Header';
import Header2 from '../Header2';
import Footer from '../Footer';
import { submitRestaurantApplication } from '../../api/starviewApi';
import { DRIVER_COUNTRY_OPTIONS } from '../../utils/driverCountryOptions';
import './RestaurantApply.css';

const DAYS = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];

const DEFAULT_HOURS = { open: '11:00', close: '21:00' };

function defaultWeeklyHours() {
  const hours = {};
  for (const d of DAYS) hours[d.key] = { ...DEFAULT_HOURS };
  return hours;
}

const EMPTY_FORM = {
  businessName: '',
  cuisine: '',
  description: '',
  ownerName: '',
  email: '',
  phoneNumber: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  region: '',
  postalCode: '',
};

export default function RestaurantApply() {
  const [country, setCountry] = useState('US');
  const [form, setForm] = useState(EMPTY_FORM);
  const [weeklyHours, setWeeklyHours] = useState(defaultWeeklyHours());
  const [closedDays, setClosedDays] = useState({});
  const [timezone, setTimezone] = useState(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { return ''; }
  });
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const updateField = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const updateHours = (day, part) => (e) => {
    setWeeklyHours((h) => ({ ...h, [day]: { ...h[day], [part]: e.target.value } }));
  };

  const toggleClosed = (day) => (e) => {
    setClosedDays((c) => ({ ...c, [day]: e.target.checked }));
  };

  // A real StarView-onboarding-specialist pass still finishes menu, photos
  // and delivery zones before a new partner actually goes live (see
  // approveRestaurantApplicationHandler's onboardingComplete: false) - but
  // getting an approximate location right away, from wherever the applicant
  // is actually standing when they fill this out, needs no separate
  // geocoding service or API key at all.
  const handleUseLocation = () => {
    if (!navigator.geolocation) {
      setLocateError('Location access is not available in this browser.');
      return;
    }
    setLocating(true);
    setLocateError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCoords({ latitude: position.coords.latitude, longitude: position.coords.longitude });
        setLocating(false);
      },
      (err) => {
        setLocateError(err.message || 'Could not get your location.');
        setLocating(false);
      },
      { timeout: 10000 }
    );
  };

  const canSubmit = form.businessName.trim() && form.ownerName.trim() && form.email.trim()
    && form.addressLine1.trim() && form.city.trim() && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const hoursPayload = {};
      for (const d of DAYS) {
        hoursPayload[d.key] = closedDays[d.key] ? null : weeklyHours[d.key];
      }
      await submitRestaurantApplication({
        country,
        ...form,
        latitude: coords?.latitude,
        longitude: coords?.longitude,
        weeklyHours: hoursPayload,
        timezone: timezone || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong submitting your application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="ra-root">
        <Header />
        <section className="ra-success">
          <div className="ra-success-badge">✓</div>
          <h1>Application Submitted</h1>
          <p>
            Thanks for applying to bring {form.businessName} to StarView OrderUp. A real reviewer checks every
            application by hand - once approved, we'll email {form.email} with a link to set your password and
            sign in to the Partner Portal.
          </p>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="ra-root">
      <Header />
      <Header2 />

      <section className="ra-hero">
        <span className="ra-eyebrow">OrderUp for Restaurants</span>
        <h1 className="ra-title">Bring Your Restaurant to StarView</h1>
        <p className="ra-subtitle">
          Reach moviegoers ordering from the theater lobby while they wait for their showtime. Apply below - it
          takes about five minutes, and a real person reviews every application.
        </p>
      </section>

      <form className="ra-form-section" onSubmit={handleSubmit}>
        <h2 className="ra-section-heading">Your Restaurant</h2>
        <div className="ra-field-grid">
          <div className="ra-field-group">
            <label className="ra-label">Business Name</label>
            <input className="ra-input" required value={form.businessName} onChange={updateField('businessName')} placeholder="Luigi's Pizzeria" />
          </div>
          <div className="ra-field-group">
            <label className="ra-label">Cuisine (comma separated)</label>
            <input className="ra-input" value={form.cuisine} onChange={updateField('cuisine')} placeholder="Italian, Pizza" />
          </div>
        </div>
        <div className="ra-field-group">
          <label className="ra-label">Description</label>
          <textarea className="ra-input ra-textarea" value={form.description} onChange={updateField('description')} placeholder="Tell us about your restaurant" />
        </div>

        <h2 className="ra-section-heading">Owner / Contact</h2>
        <div className="ra-field-grid">
          <div className="ra-field-group">
            <label className="ra-label">Your Name</label>
            <input className="ra-input" required value={form.ownerName} onChange={updateField('ownerName')} placeholder="Jane Doe" />
          </div>
          <div className="ra-field-group">
            <label className="ra-label">Email</label>
            <input className="ra-input" type="email" required value={form.email} onChange={updateField('email')} placeholder="jane@example.com" />
          </div>
          <div className="ra-field-group">
            <label className="ra-label">Phone Number</label>
            <input className="ra-input" type="tel" value={form.phoneNumber} onChange={updateField('phoneNumber')} placeholder="(555) 555-0100" />
          </div>
        </div>

        <h2 className="ra-section-heading">Location</h2>
        <div className="ra-field-grid">
          <div className="ra-field-group">
            <label className="ra-label">Country</label>
            <select className="ra-input" value={country} onChange={(e) => setCountry(e.target.value)}>
              {DRIVER_COUNTRY_OPTIONS.map((c) => (
                <option key={c.iso} value={c.iso}>{c.label}</option>
              ))}
            </select>
          </div>
          <div className="ra-field-group">
            <label className="ra-label">Address Line 1</label>
            <input className="ra-input" required value={form.addressLine1} onChange={updateField('addressLine1')} placeholder="123 Main St" />
          </div>
          <div className="ra-field-group">
            <label className="ra-label">Address Line 2</label>
            <input className="ra-input" value={form.addressLine2} onChange={updateField('addressLine2')} placeholder="Suite 4 (optional)" />
          </div>
          <div className="ra-field-group">
            <label className="ra-label">City</label>
            <input className="ra-input" required value={form.city} onChange={updateField('city')} placeholder="Springfield" />
          </div>
          <div className="ra-field-group">
            <label className="ra-label">State / Region</label>
            <input className="ra-input" value={form.region} onChange={updateField('region')} placeholder="Optional" />
          </div>
          <div className="ra-field-group">
            <label className="ra-label">Postal Code</label>
            <input className="ra-input" value={form.postalCode} onChange={updateField('postalCode')} placeholder="Optional" />
          </div>
        </div>
        <div className="ra-locate-row">
          <button type="button" className="ra-locate-btn" onClick={handleUseLocation} disabled={locating}>
            {locating ? 'Locating…' : coords ? '✓ Location Captured' : 'Pin My Current Location'}
          </button>
          {locateError && <span className="ra-locate-error">{locateError}</span>}
          {!coords && !locateError && (
            <span className="ra-locate-hint">Best done from the restaurant itself - helps us place you accurately on the map.</span>
          )}
        </div>

        <h2 className="ra-section-heading">Hours</h2>
        <div className="ra-field-group">
          <label className="ra-label">Timezone</label>
          <input className="ra-input" style={{ maxWidth: 280 }} value={timezone} onChange={(e) => setTimezone(e.target.value)} placeholder="e.g. America/New_York" />
        </div>
        <div className="ra-hours-grid">
          {DAYS.map((d) => (
            <div className="ra-hours-row" key={d.key}>
              <span className="ra-hours-day">{d.label}</span>
              {closedDays[d.key] ? (
                <span className="ra-hours-closed">Closed</span>
              ) : (
                <>
                  <input className="ra-input ra-hours-input" type="time" value={weeklyHours[d.key].open} onChange={updateHours(d.key, 'open')} />
                  <span className="ra-hours-to">to</span>
                  <input className="ra-input ra-hours-input" type="time" value={weeklyHours[d.key].close} onChange={updateHours(d.key, 'close')} />
                </>
              )}
              <label className="ra-hours-closed-toggle">
                <input type="checkbox" checked={!!closedDays[d.key]} onChange={toggleClosed(d.key)} /> Closed
              </label>
            </div>
          ))}
        </div>

        {submitError && <p className="ra-submit-error">{submitError}</p>}
        <button type="submit" className="ra-submit-btn" disabled={!canSubmit}>
          {submitting ? 'Submitting…' : 'Submit Application'}
        </button>
      </form>

      <Footer />
    </div>
  );
}
